import { toast } from "react-toastify";

import apiClient from "../../apis/api-client";
import { getFriendlyErrorMessage } from "../../util/error-message-mapper";
import type { AuthType } from "../../domain/auth/auth";
import {
  getGroupIdToQueryProc,
  Jurisdiction,
  Paging,
  Status,
  StatusValue,
} from "../../domain/proc-management";
import { BaseApplicationService } from "../../domain/base/base-application-service";
import { reportProcManagementRelatedError } from "../../domain/proc-management/util/error";

export const defaultFetchStatuses = [Status.Draft, Status.Published] as const;
const defaultLimit = 10;
const defaultSort = { by: "createdAt", order: "desc" } as const;

export type fetchProcsParam = {
  organizationId: string;
  page?: number;
  query?: string;
  statuses: StatusValue[];
};

export type ContextProps = {
  procedures: {
    id: string;
    name: string;
    contentStatus: StatusValue;
    jurisdiction: Jurisdiction;
  }[];
  paging: Paging;
  fetchProcsByOrgId: (parm: fetchProcsParam) => void;

  authCtx: AuthType["data"];
  setAuthCtx: (authCtx: ContextProps["authCtx"]) => void;
};

export class BaseProcManagementListService<
  P extends Record<string, any>,
> extends BaseApplicationService<P & ContextProps> {
  constructor(rest: P & Partial<ContextProps>) {
    super();
    this.state = {
      procedures: [],
      paging: { current: 1, last: 1 },
      fetchProcsByOrgId: this.fetchProcsByOrgId.bind(this),

      authCtx: undefined,
      setAuthCtx: this.setAuthCtx.bind(this),

      ...rest,
    };
  }

  async fetchProcsByOrgId({
    organizationId,
    page,
    query,
    statuses,
  }: fetchProcsParam) {
    const { authCtx } = this.state;
    if (!authCtx) {
      return;
    }
    const result = getGroupIdToQueryProc(authCtx);
    if (!result.canQuery) {
      toast.error(result.message);
      return;
    }
    const { groupIds } = result;
    const fetcher =
      typeof query === "string" && query.length > 0
        ? () => {
            return apiClient.procManagement.searchProcs({
              organizationId,
              groupIds,
              query,
              statuses,
              page,
              limit: defaultLimit,
            });
          }
        : () => {
            return apiClient.procManagement.listProcs({
              organizationId,
              groupIds,
              statuses,
              page,
              limit: defaultLimit,
              sort: defaultSort,
            });
          };

    try {
      const { data } = await fetcher();
      this.setState((prev) => {
        return {
          ...prev,
          procedures: "hits" in data ? data.hits : data.items,
          paging: data.paging,
        };
      });
    } catch (error) {
      reportProcManagementRelatedError(error);
      const friendlyMessage = getFriendlyErrorMessage(error);
      toast.error(`手続きデータの取得に失敗しました: ${friendlyMessage}`);
    }
  }

  setAuthCtx(authCtx: ContextProps["authCtx"]) {
    this.setState((prev) => {
      return {
        ...prev,
        authCtx,
      };
    });
  }
}
